import type { DictionaryEntry } from "./types";
import { config, DICTIONARY_MODE } from "./config";

// CC-Canto format: Traditional Simplified [Pinyin] {Jyutping} /definitions/
const CANTO_REGEX =
  /^(\S+)\s+(\S+)\s+\[([^\]]*)\]\s+\{([^}]+)\}\s+\/(.+)\/\s*(?:#.*)?$/;

// CC-CEDICT format: Traditional Simplified [pin1 yin1] /definitions/
const CEDICT_REGEX = /^(\S+)\s+(\S+)\s+\[([^\]]+)\]\s+\/(.+)\/\s*$/;

export function parseCantoEntry(line: string): DictionaryEntry | null {
  // Skip comments and empty lines
  if (line.startsWith("#") || line.trim() === "") {
    return null;
  }

  const match = line.match(CANTO_REGEX);
  if (!match) {
    return null;
  }

  const [, traditional, , , jyutping, definitionStr] = match;

  // Split definitions by '/' and filter empty strings
  const definitions = definitionStr
    .split("/")
    .map((d) => d.trim())
    .filter((d) => d.length > 0);

  return {
    traditional,
    jyutping,
    definitions,
  };
}

export function parseCedictEntry(line: string): DictionaryEntry | null {
  // Skip comments and empty lines
  if (line.startsWith("#") || line.trim() === "") {
    return null;
  }

  const match = line.match(CEDICT_REGEX);
  if (!match) {
    return null;
  }

  const [, traditional, , pinyin, definitionStr] = match;

  // Split definitions by '/' and filter empty strings
  const definitions = definitionStr
    .split("/")
    .map((d) => d.trim())
    .filter((d) => d.length > 0);

  // Store pinyin in the jyutping field (it's the pronunciation field)
  return {
    traditional,
    jyutping: pinyin,
    definitions,
  };
}

export function parseEntry(line: string): DictionaryEntry | null {
  return DICTIONARY_MODE === "mandarin"
    ? parseCedictEntry(line)
    : parseCantoEntry(line);
}

export async function loadDictionary(): Promise<DictionaryEntry[]> {
  const response = await fetch(config.dictionaryFile);
  if (!response.ok) {
    throw new Error(`Failed to load dictionary: ${response.statusText}`);
  }

  const text = await response.text();
  const lines = text.split("\n");

  const entries: DictionaryEntry[] = [];
  for (const line of lines) {
    const entry = parseEntry(line);
    if (entry) {
      entries.push(entry);
    }
  }

  return entries;
}
